import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, IconButton, Switch, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import getServices from '../../Services/get-service';
import putServices from '../../Services/put-service';
import swal from 'sweetalert';
import Loading from '../SelectValue/Loading';
import Constant from '../../Constant';
import TopDCom from '../Savings.js/TopDCom';
import AddAssets from './AddAssests';

const statusOptions = ['ACTIVE', 'INACTIVE'];

const getAssetId = (asset) => asset.id || asset._id;

const getAssetName = (asset) => asset.name || asset.assetName || 'N/A';

const getAssetStatus = (asset) => {
  if (asset.status) return asset.status;
  if (asset.assetStatus === 'available') return 'ACTIVE';
  if (asset.assetStatus) return asset.assetStatus;
  return 'N/A';
};

const getStatusClass = (status) => {
  return status === 'ACTIVE' || status === 'available' ? 'text-green-700' : 'text-red-600';
};

const isActiveAsset = (asset) => {
  const status = getAssetStatus(asset);
  return status === 'ACTIVE' || status === 'available' || status === 'active';
};

export default function AssestsTable({ titleFilter, readOnly = false }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [search, setSearch] = useState(titleFilter || '');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const fetchAssets = useCallback(() => {
    setLoading(true);
    getServices.getAssetProducts({
      status,
      categoryId,
      page,
      limit: rowsPerPage,
      q: search,
    }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setAssets(response.data.data?.assets || []);
          setPagination(response.data.data?.pagination || null);
          return;
        }

        swal(response.data.description || 'Failed to fetch assets');
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error fetching assets');
      }
    );
  }, [categoryId, page, rowsPerPage, search, status]);

  const fetchAssetStats = useCallback(() => {
    getServices.getAssetProductStats().then(
      (response) => {
        if (response.data.status === '2000') {
          setAssetStats(response.data.data || null);
        } else {
          swal(response.data.description || 'Failed to fetch asset stats');
        }
      },
      (error) => {
        swal(error?.response?.data?.description || 'Error fetching asset stats');
      }
    );
  }, []);

  useEffect(() => {
    getServices.getAssestCategory().then(
      (response) => {
        if (response.data.status === '2000') {
          setCategories(response.data.data || []);
        } else {
          swal(response.data.description || 'Failed to fetch categories');
        }
      },
      (error) => {
        console.error('Error fetching categories:', error);
        swal('Error fetching categories');
      }
    );
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    fetchAssetStats();
  }, [fetchAssetStats]);

  useEffect(() => {
    setSearch(titleFilter || '');
    setPage(0);
  }, [titleFilter]);

  const editAsset = (asset) => {
    if (readOnly) {
      return;
    }

    setSelectedAsset(asset);
    setEditDrawerOpen(true);
  };

  const toggleAssetStatus = (asset, shouldActivate) => {
    if (readOnly) {
      return;
    }

    const assetId = getAssetId(asset);

    if (!assetId) {
      return;
    }

    const action = shouldActivate ? 'activate' : 'deactivate';
    const actionPastTense = shouldActivate ? 'activated' : 'deactivated';
    const actionProgressive = shouldActivate ? 'activating' : 'deactivating';

    swal({
      title: `${shouldActivate ? 'Activate' : 'Deactivate'} asset?`,
      text: `This will ${action} the asset.`,
      icon: 'warning',
      buttons: ['Cancel', shouldActivate ? 'Activate' : 'Deactivate'],
      dangerMode: !shouldActivate,
    }).then((confirmed) => {
      if (!confirmed) return;

      setLoading(true);
      putServices.toggleAssetProduct(assetId).then(
        (response) => {
          setLoading(false);

          if (response.data.status === '2000') {
            swal(response.data.description || `Asset ${actionPastTense} successfully.`).then(() => {
              fetchAssets();
              fetchAssetStats();
            });
            return;
          }

          swal(response.data.description || `Failed to ${action} asset`);
        },
        (error) => {
          setLoading(false);
          swal(error?.response?.data?.description || `Error ${actionProgressive} asset`);
        }
      );
    });
  };

  const handlePreviousPage = () => {
    if (pagination?.hasPrevious || page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setPage(page + 1);
    }
  };

  const currentPage = pagination?.currentPage ?? page;
  const totalPages = pagination?.totalPages || 1;

  return (
    <>
      <Loading open={loading} />
      <AddAssets
        open2={editDrawerOpen}
        handleClose2={() => {
          setEditDrawerOpen(false);
          setSelectedAsset(null);
        }}
        assetToEdit={selectedAsset}
        onSaved={() => {
          fetchAssets();
          fetchAssetStats();
        }}
      />

      <div className="my-8 flex flex-wrap items-center gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="w-full sm:w-auto lg:col-span-2">
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search by asset name"
              className="border border-gray-300 focus:border-primary-color outline-none p-3 rounded w-full"
            />
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
                setPage(0);
              }}
              className="border w-full p-3 rounded bg-white"
            >
              <option value="">Asset category</option>
              {categories.map((category) => (
                <option key={category.id || category._id} value={category.id || category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
              className="border w-full p-3 rounded bg-white"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option.charAt(0) + option.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 md:grid-cols-5 mb-10 gap-4">
        <TopDCom name="Total assets in inventory" count={assetStats?.totalAssetsInInventory ?? 0} />
        <TopDCom name="Assets sold" count={assetStats?.assetsSold ?? 0} />
        <TopDCom name="Assets amount sold" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountSold || 0)}`} />
        <TopDCom name="Assets amount paid" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountPaid || 0)}`} />
        <TopDCom name="Assets amount outstanding" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountOutstanding || 0)}`} />
      </div>

      <div className="mt-16">
        <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4">
          <Checkbox />
          <p className="text-text flex-1 text-center whitespace-normal break-words">Asset Name</p>
          {/* <p className="text-text flex-1 text-center">Date Added</p> */}
          <p className="text-text flex-1 text-center whitespace-normal break-words">Quantity</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Item Amount</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Status</p>
          {!readOnly && <p className="text-text flex-1 text-center whitespace-normal break-words">Action</p>}
        </div>

        {loading ? (
          <div>
            {[...Array(rowsPerPage)].map((_, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-b border-gray-200 p-4 gap-2"
              >
                <div className="w-6 md:w-[5%]">
                  <Skeleton width={24} height={24} />
                </div>
                {[...Array(readOnly ? 4 : 5)].map((__, skeletonIndex) => (
                  <div key={skeletonIndex} className="flex-1 text-left md:text-center">
                    <Skeleton width="70%" height={20} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center p-4 text-gray-500">
            No assets found.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {assets.map((row) => {
                const rowStatus = getAssetStatus(row);

                return (
                  <div key={getAssetId(row)} className="border rounded-lg p-4 mb-3 bg-white shadow-sm">
                    <p className="font-semibold capitalize">{getAssetName(row)}</p>
                    {/* <p><b>Date Added:</b> {formatDate(row.createdDate || row.createdAt)}</p> */}
                    <p><b>Quantity:</b> {row.totalUnits}</p>
                    <p><b>Item Amount:</b> {Constant.FormatNumber(row.unitPrice)}</p>
                    <p>
                      <b>Status:</b>{' '}
                      <span className={getStatusClass(rowStatus)}>
                        {rowStatus}
                      </span>
                    </p>
                    {!readOnly && (
                      <div className="flex justify-end items-center gap-3 mt-2">
                        <Tooltip title="Edit asset">
                          <IconButton
                            size="small"
                            onClick={() => editAsset(row)}
                            aria-label="Edit asset"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isActiveAsset(row) ? 'Deactivate asset' : 'Activate asset'}>
                          <Switch
                            checked={isActiveAsset(row)}
                            onChange={(event) => toggleAssetStatus(row, event.target.checked)}
                            inputProps={{ 'aria-label': isActiveAsset(row) ? 'Deactivate asset' : 'Activate asset' }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#16a34a',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#16a34a',
                              },
                            }}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block">
              {assets.map((row) => {
                const rowStatus = getAssetStatus(row);

                return (
                  <div key={getAssetId(row)} className="flex flex-row justify-between items-center bg-white border-b border-gray-200 p-4">
                    <Checkbox />
                    <div className="flex-1 text-center capitalize break-words whitespace-normal min-w-0 px-1">{getAssetName(row)}</div>
                    {/* <p className="flex-1 text-center">{formatDate(row.createdDate || row.createdAt)}</p> */}
                    <p className="flex-1 text-center text-primary-color break-words whitespace-normal min-w-0 px-1">{row.totalUnits}</p>
                    <p className="flex-1 text-center text-primary-color break-words whitespace-normal min-w-0 px-1">
                      {Constant.FormatNumber(row.unitPrice)}
                    </p>
                    <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                      <span className={getStatusClass(rowStatus)}>
                        {rowStatus}
                      </span>
                    </p>
                    {!readOnly && (
                      <div className="flex-1 flex justify-center items-center gap-3 break-words whitespace-normal min-w-0 px-1">
                        <Tooltip title="Edit asset">
                          <IconButton
                            size="small"
                            onClick={() => editAsset(row)}
                            aria-label="Edit asset"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isActiveAsset(row) ? 'Deactivate asset' : 'Activate asset'}>
                          <Switch
                            checked={isActiveAsset(row)}
                            onChange={(event) => toggleAssetStatus(row, event.target.checked)}
                            inputProps={{ 'aria-label': isActiveAsset(row) ? 'Deactivate asset' : 'Activate asset' }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#16a34a',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#16a34a',
                              },
                            }}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-8 mb-10">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg border ${
              currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Previous
          </button>
          <p className="text-text text-sm sm:text-base">
            Page {currentPage + 1} of {totalPages}
          </p>
          <button
            onClick={handleNextPage}
            disabled={!pagination?.hasNext}
            className={`px-4 py-2 rounded-lg border ${
              !pagination?.hasNext
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
